// Academic Year CRUD operations using direct fetch (bypassing Supabase client)

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getSchoolContext } from './school-context'
import { getAccessToken } from './auth-token'
import type {
  AcademicYear,
  CreateAcademicYearRequest,
  UpdateAcademicYearRequest,
  AcademicYearFilters,
  DuplicateAcademicYearRequest,
  DuplicateAcademicYearResponse,
  PublishAcademicYearRequest,
  ArchiveAcademicYearRequest,
} from '@timetable/types'

const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

export async function getAcademicYearsDirect(filters: AcademicYearFilters = {}): Promise<AcademicYear[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const schoolContext = getSchoolContext()
    const effectiveSchoolId = filters.school_id || schoolContext

    const params = new URLSearchParams()
    params.append('select', '*')

    if (effectiveSchoolId) {
      params.append('school_id', `eq.${effectiveSchoolId}`)
    }
    if (filters.status !== undefined) {
      params.append('status', `eq.${filters.status}`)
    }
    params.append('order', 'start_date.desc')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${params}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch academic years')
    }

    return await response.json()
  })
}

export async function getAcademicYearByIdDirect(id: string): Promise<AcademicYear> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('id', `eq.${id}`)

    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Academic year not found')
    }

    return await response.json()
  })
}

/** Get the currently published academic year for a school */
export async function getActiveDirect(schoolId: string): Promise<AcademicYear | null> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')
    params.append('school_id', `eq.${schoolId}`)
    params.append('status', 'eq.published')
    params.append('limit', '1')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${params}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch published academic year')
    }

    const data = await response.json()
    return data.length > 0 ? data[0] : null
  })
}

/** Create a new academic year (always starts as draft via schema default) */
export async function createAcademicYearDirect(academicYear: CreateAcademicYearRequest): Promise<AcademicYear> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const startDate = new Date(academicYear.start_date)
    const endDate = new Date(academicYear.end_date)

    if (startDate >= endDate) {
      throw new Error('Start date must be before end date')
    }

    // Do NOT include is_active — schema default handles status = 'draft'
    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(academicYear),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create academic year')
    }

    const data: any[] = await response.json()
    return data[0]
  })
}

export async function updateAcademicYearDirect(id: string, updates: UpdateAcademicYearRequest): Promise<AcademicYear> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    if (updates.start_date || updates.end_date) {
      const current = await getAcademicYearByIdDirect(id)
      const startDate = new Date(updates.start_date || current.start_date)
      const endDate = new Date(updates.end_date || current.end_date)

      if (startDate >= endDate) {
        throw new Error('Start date must be before end date')
      }
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?id=eq.${id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(updates),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to update academic year')
    }

    const data: any[] = await response.json()
    if (!data[0]) throw new Error('Academic year not found')

    // Sync academic_year text on timetable_records when name changes (Req 20.1, 20.3)
    // The DB trigger trg_sync_academic_year_text handles this at schema level;
    // this API-layer sync is a belt-and-suspenders fallback.
    if (updates.name) {
      await fetch(
        `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${id}`,
        {
          method: 'PATCH',
          headers: {
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ academic_year: updates.name }),
        }
      )
    }

    return data[0]
  })
}

/**
 * Inline time-overlap conflict counter for pre-publish validation.
 * Mirrors the logic in conflictDetection.ts but runs in the API layer
 * without a UI dependency. Returns the total number of conflicts found.
 */
function _detectConflictsForPublish(records: any[]): number {
  let count = 0

  function overlaps(aStart: string, aEnd: string, bStart: string, bEnd: string): boolean {
    return aStart < bEnd && bStart < aEnd
  }

  // Teacher conflicts
  const byTeacher = new Map<string, any[]>()
  for (const r of records) {
    if (!r.teacher_id) continue
    if (!byTeacher.has(r.teacher_id)) byTeacher.set(r.teacher_id, [])
    byTeacher.get(r.teacher_id)!.push(r)
  }
  for (const teacherRecords of byTeacher.values()) {
    const byDay = new Map<string, any[]>()
    for (const r of teacherRecords) {
      if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, [])
      byDay.get(r.day_of_week)!.push(r)
    }
    for (const dayRecords of byDay.values()) {
      for (let i = 0; i < dayRecords.length; i++) {
        for (let j = i + 1; j < dayRecords.length; j++) {
          const a = dayRecords[i]; const b = dayRecords[j]
          if (a.period?.time_start && a.period?.time_end && b.period?.time_start && b.period?.time_end) {
            if (overlaps(a.period.time_start, a.period.time_end, b.period.time_start, b.period.time_end)) count++
          }
        }
      }
    }
  }

  // Room conflicts
  const byRoom = new Map<string, any[]>()
  for (const r of records) {
    if (!r.room_id) continue
    if (!byRoom.has(r.room_id)) byRoom.set(r.room_id, [])
    byRoom.get(r.room_id)!.push(r)
  }
  for (const roomRecords of byRoom.values()) {
    const byDay = new Map<string, any[]>()
    for (const r of roomRecords) {
      if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, [])
      byDay.get(r.day_of_week)!.push(r)
    }
    for (const dayRecords of byDay.values()) {
      for (let i = 0; i < dayRecords.length; i++) {
        for (let j = i + 1; j < dayRecords.length; j++) {
          const a = dayRecords[i]; const b = dayRecords[j]
          if (a.period?.time_start && a.period?.time_end && b.period?.time_start && b.period?.time_end) {
            if (overlaps(a.period.time_start, a.period.time_end, b.period.time_start, b.period.time_end)) count++
          }
        }
      }
    }
  }

  // Class section conflicts
  const bySection = new Map<string, any[]>()
  for (const r of records) {
    if (!r.class_section_id) continue
    if (!bySection.has(r.class_section_id)) bySection.set(r.class_section_id, [])
    bySection.get(r.class_section_id)!.push(r)
  }
  for (const sectionRecords of bySection.values()) {
    const byDay = new Map<string, any[]>()
    for (const r of sectionRecords) {
      if (!byDay.has(r.day_of_week)) byDay.set(r.day_of_week, [])
      byDay.get(r.day_of_week)!.push(r)
    }
    for (const dayRecords of byDay.values()) {
      for (let i = 0; i < dayRecords.length; i++) {
        for (let j = i + 1; j < dayRecords.length; j++) {
          const a = dayRecords[i]; const b = dayRecords[j]
          if (a.period?.time_start && a.period?.time_end && b.period?.time_start && b.period?.time_end) {
            if (overlaps(a.period.time_start, a.period.time_end, b.period.time_start, b.period.time_end)) count++
          }
        }
      }
    }
  }

  return count
}

/**
 * Publish a draft academic year.
 * Runs pre-publish validation, auto-archives the current published year,
 * then transitions the target to published.
 * Requirements: 5.1, 5.2, 6.2, 23.3
 */
export async function publishAcademicYear(request: PublishAcademicYearRequest): Promise<AcademicYear> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const year = await getAcademicYearByIdDirect(request.id)

    if (year.school_id !== request.schoolId) {
      throw new Error('Academic year does not belong to the specified school')
    }

    if ((year as any).status !== 'draft') {
      throw new Error(`Cannot publish: academic year is currently '${(year as any).status}'. Only draft years can be published.`)
    }

    // Pre-publish validation: at least one timetable record must exist
    const recordsCheck = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${request.id}&select=id&limit=1`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    if (recordsCheck.ok) {
      const records = await recordsCheck.json()
      if (!records || records.length === 0) {
        throw new Error('Cannot publish: no timetable records exist for this academic year.')
      }
    }

    // Pre-publish validation: all classes with records must have class_schedule_id
    const classesCheck = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${request.id}&select=class_section:class_sections(class:classes(id,class_name,class_schedule_id))`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    if (classesCheck.ok) {
      const records = await classesCheck.json()
      const missingSchedule = records.filter(
        (r: any) => r.class_section?.class && !r.class_section.class.class_schedule_id
      )
      if (missingSchedule.length > 0) {
        const classNames = [...new Set(missingSchedule.map((r: any) => r.class_section.class.class_name))].join(', ')
        throw new Error(`Cannot publish: the following classes have no schedule assigned: ${classNames}`)
      }
    }

    // Pre-publish validation: zero scheduling conflicts
    const conflictRecordsRes = await fetch(
      `${SUPABASE_URL}/rest/v1/timetable_records?academic_year_id=eq.${request.id}&select=id,teacher_id,room_id,class_section_id,day_of_week,period:schedule_periods(id,time_start,time_end,period_number)`,
      { headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` } }
    )
    if (conflictRecordsRes.ok) {
      const conflictRecords: any[] = await conflictRecordsRes.json()
      const conflicts = _detectConflictsForPublish(conflictRecords)
      if (conflicts > 0) {
        throw new Error(`Cannot publish: ${conflicts} scheduling conflict(s) exist. Resolve all conflicts before publishing.`)
      }
    }

    // Auto-archive any currently published year for this school
    await fetch(
      `${SUPABASE_URL}/rest/v1/academic_years?school_id=eq.${request.schoolId}&status=eq.published&id=neq.${request.id}`,
      {
        method: 'PATCH',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'archived' }),
      }
    )

    // Publish the target year
    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?id=eq.${request.id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ status: 'published' }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to publish academic year')
    }

    const data: any[] = await response.json()
    return data[0]
  })
}

/**
 * Archive a published academic year.
 * Requirements: 5.3, 5.4, 5.5, 23.3
 */
export async function archiveAcademicYear(request: ArchiveAcademicYearRequest): Promise<AcademicYear> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const year = await getAcademicYearByIdDirect(request.id)

    if ((year as any).status === 'archived') {
      throw new Error('Academic year is already archived. Archived years are permanent.')
    }
    if ((year as any).status === 'draft') {
      throw new Error('Cannot archive a draft year. Only published years can be archived.')
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?id=eq.${request.id}`, {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify({ status: 'archived' }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to archive academic year')
    }

    const data: any[] = await response.json()
    return data[0]
  })
}

/** Delete an academic year (only draft years with no timetable records) */
export async function deleteAcademicYearDirect(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    // Check by UUID FK (academic_year_id), not the old text field
    const checkParams = new URLSearchParams()
    checkParams.append('select', 'id')
    checkParams.append('academic_year_id', `eq.${id}`)
    checkParams.append('limit', '1')

    const checkResponse = await fetch(`${SUPABASE_URL}/rest/v1/timetable_records?${checkParams}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })

    if (checkResponse.ok) {
      const records = await checkResponse.json()
      if (records && records.length > 0) {
        throw new Error('Cannot delete academic year with existing timetable records')
      }
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?id=eq.${id}`, {
      method: 'DELETE',
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete academic year')
    }
  })
}

/**
 * Duplicate an academic year's timetable to a new draft academic year.
 * Uses UUID-based RPC parameters (p_source_year_id / p_target_year_id).
 * Requirements: 9.1–9.4, 63.1, 63.2
 */
export async function duplicateAcademicYearDirect(
  request: DuplicateAcademicYearRequest
): Promise<DuplicateAcademicYearResponse> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const sessionStr = window.localStorage.getItem('supabase.auth.token')
    if (!sessionStr) throw new Error('Not authenticated')

    const session = JSON.parse(sessionStr)
    const userId = session?.currentSession?.user?.id
    if (!userId) throw new Error('User ID not found')

    const userParams = new URLSearchParams()
    userParams.append('select', 'school_id')
    userParams.append('id', `eq.${userId}`)

    const userResponse = await fetch(`${SUPABASE_URL}/rest/v1/users?${userParams}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Accept': 'application/vnd.pgrst.object+json',
      },
    })

    if (!userResponse.ok) {
      throw new Error('Failed to get user school context')
    }

    const userData: any = await userResponse.json()
    const schoolId = userData.school_id

    // Validate target year is draft before calling RPC
    const targetYear = await getAcademicYearByIdDirect(request.target_year_id)
    if ((targetYear as any).status !== 'draft') {
      throw new Error('Target year must be in draft status to duplicate into it')
    }

    // Reject if target year name already exists for this school (Req 9.4)
    const nameCheckParams = new URLSearchParams()
    nameCheckParams.append('select', 'id')
    nameCheckParams.append('school_id', `eq.${schoolId}`)
    nameCheckParams.append('name', `eq.${targetYear.name}`)
    nameCheckParams.append('id', `neq.${request.target_year_id}`)
    nameCheckParams.append('limit', '1')

    const nameCheckRes = await fetch(`${SUPABASE_URL}/rest/v1/academic_years?${nameCheckParams}`, {
      headers: { 'apikey': SUPABASE_ANON_KEY, 'Authorization': `Bearer ${accessToken}` },
    })
    if (nameCheckRes.ok) {
      const existing = await nameCheckRes.json()
      if (existing && existing.length > 0) {
        throw new Error(`An academic year named "${targetYear.name}" already exists for this school.`)
      }
    }

    const targetStartDate = new Date(request.target_start_date)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (targetStartDate < today) {
      throw new Error('Target start date cannot be in the past')
    }

    // Call RPC with UUID parameters
    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/duplicate_academic_year`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        p_school_id: schoolId,
        p_source_year_id: request.source_year_id,
        p_target_year_id: request.target_year_id,
        p_target_start_date: request.target_start_date,
        p_created_by: request.created_by || userId,
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to duplicate academic year')
    }

    const result = await response.json()

    return {
      created: result.created,
      skipped: result.skipped,
      errors: result.errors,
    }
  })
}
